import {
  CloseCircleOutlined,
  CloseOutlined,
  LeftOutlined,
  MinusOutlined,
  ReloadOutlined,
  RightOutlined,
  VerticalLeftOutlined,
  VerticalRightOutlined,
} from "@ant-design/icons";

import { useLocation, useNavigate } from "@umijs/max";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import "./index.less";

import type { ContextMenuPosition, TabItem } from "./types";

const HOME_PATH = "/dashboard/analysis";

// ✅ 路由稳定延迟：解决“先跳A再立刻redirect到B”导致的多开tab

const STABLE_ROUTE_DELAY = 80;

/**

 * ✅ 关键：把“别名/中间路由”统一映射到最终真实页面路由

 * 你这个 case：个人管理 => 实际展示用户管理，所以 canonical 直接指向 user-management

 * 你也可以按项目实际补充更多 alias

 */

const CANONICAL_PATH_MAP: Record<string, string> = {
  "/personal/management": "/personal/user-management",

  "/personal": "/personal/user-management",

  "/personal/": "/personal/user-management",
};

function normalizePath(pathname: string) {
  return CANONICAL_PATH_MAP[pathname] || pathname;
}

const TabsView: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  const navigate = useNavigate();

  // 空实现，不使用缓存
  const drop = (_key: string) => Promise.resolve();
  const refresh = (_key: string) => Promise.resolve();

  const [tabs, setTabs] = useState<TabItem[]>([]);

  const [activeKey, setActiveKey] = useState<string>("");

  const [isTransitioning, setIsTransitioning] = useState(false);

  const [contextMenu, setContextMenu] = useState<ContextMenuPosition | null>(
    null
  );

  const [refreshingKey, setRefreshingKey] = useState<string | null>(null);

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const tabsListRef = useRef<HTMLDivElement>(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);

  const [canScrollRight, setCanScrollRight] = useState(false);

  const getPageTitle = useCallback((pathname: string): string => {
    const titleMap: Record<string, string> = {
      "/dashboard/analysis": "仪表盘",

      "/welcome": "欢迎",

      "/admin/sub-page": "管理页",

      "/list": "查询表格",

      "/reduction": "减员管理",

      "/insurance": "参保管理",
      "/recharge": "充值管理",

      "/personal/user-profile": "用户档案",

      "/personal/user-management": "用户管理",

      // “个人管理”如果最终展示用户管理，那就不要单独开一个标题

      // "/personal/management": "个人管理",  // <- 不建议开
    };

    return titleMap[pathname] || pathname.split("/").pop() || pathname;
  }, []);

  const makeTabKey = useCallback((pathname: string, search?: string) => {
    const p = normalizePath(pathname);

    const s = search || "";

    return `${p}${s}`;
  }, []);

  const checkScrollable = useCallback(() => {
    requestAnimationFrame(() => {
      const container = tabsListRef.current;

      if (!container) return;

      const left = container.scrollLeft > 0;

      const right =
        container.scrollLeft <
        container.scrollWidth - container.clientWidth - 1;

      setCanScrollLeft((prev) => (prev !== left ? left : prev));

      setCanScrollRight((prev) => (prev !== right ? right : prev));
    });
  }, []);

  useEffect(() => {
    checkScrollable();

    window.addEventListener("resize", checkScrollable);

    return () => window.removeEventListener("resize", checkScrollable);
  }, [checkScrollable, tabs]);

  const ensureTab = useCallback(
    (tabKey: string, canonicalPathname: string) => {
      setTabs((prev) => {
        if (prev.some((t) => t.key === tabKey)) return prev;

        const newTab: TabItem = {
          key: tabKey,

          path: canonicalPathname,

          title: getPageTitle(canonicalPathname),

          closable: canonicalPathname !== HOME_PATH,
        };

        return [...prev, newTab];
      });

      setActiveKey(tabKey);
    },

    [getPageTitle]
  );

  /**

   * ✅ 解决你说的：点个人管理 -> 页面还是用户管理，但新增一个tab

   * - 先 normalize 成 canonical path

   * - 再延迟落 tab：如果中间发生 redirect，timer 到点时 key 已变化，直接跳过

   */

  useEffect(() => {
    const rawPath = location.pathname;

    if (rawPath === "/user/login" || rawPath.startsWith("/user/")) return;

    const canonicalPath = normalizePath(rawPath);

    const rawKey = makeTabKey(canonicalPath, location.search);

    // 路由变化时触发过渡效果
    if (activeKey && rawKey !== activeKey) {
      // 立即开始过渡并刷新内容
      setIsTransitioning(true);
      setRefreshTrigger((prev) => prev + 1);

      // 200ms后完成过渡
      setTimeout(() => setIsTransitioning(false), 200);
    }

    const timer = window.setTimeout(() => {
      // timer 触发时再取一次当前路由，如果已被 redirect 改掉，就不创建“中间tab”

      const curCanonical = normalizePath(location.pathname);

      const curKey = makeTabKey(curCanonical, location.search);

      if (curKey !== rawKey) return;

      ensureTab(rawKey, canonicalPath);
    }, STABLE_ROUTE_DELAY);

    return () => window.clearTimeout(timer);
  }, [
    location.pathname,
    location.search,
    ensureTab,
    makeTabKey,
    location,
    activeKey,
  ]);

  const handleTabClick = useCallback(
    (key: string) => {
      if (key === activeKey) return;

      // 立即开始过渡并导航
      setIsTransitioning(true);
      navigate(key);
      setRefreshTrigger((prev) => prev + 1);

      // 200ms后完成过渡
      setTimeout(() => setIsTransitioning(false), 200);
    },

    [activeKey, navigate]
  );

  const handleClose = useCallback(
    (key: string, e: React.MouseEvent) => {
      e.stopPropagation();

      setTabs((prev) => {
        if (prev.length <= 1) return prev;

        const idx = prev.findIndex((t) => t.key === key);

        const nextTabs = prev.filter((t) => t.key !== key);

        drop(key).catch(() => {});

        if (key === activeKey) {
          const nextIndex = Math.min(idx, nextTabs.length - 1);

          navigate(nextTabs[nextIndex]?.key || HOME_PATH);
        }

        return nextTabs;
      });
    },

    [activeKey, navigate, drop]
  );

  const handleRefresh = useCallback(
    (key: string, e: React.MouseEvent) => {
      e.stopPropagation();

      setRefreshingKey(key);

      if (key === activeKey) {
        setRefreshTrigger((prev) => prev + 1);

        refresh(key).catch(() => {});

        setTimeout(() => navigate(key, { replace: true }), 60);
      } else {
        refresh(key).catch(() => {});
      }

      setTimeout(() => setRefreshingKey(null), 450);
    },

    [refresh, activeKey, navigate]
  );

  const handleContextMenu = useCallback(
    (e: React.MouseEvent, tabKey: string) => {
      e.preventDefault();

      setContextMenu({ x: e.clientX, y: e.clientY, tabKey });
    },

    []
  );

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  useEffect(() => {
    if (!contextMenu) return;

    const handleClick = () => closeContextMenu();

    document.addEventListener("click", handleClick);

    return () => document.removeEventListener("click", handleClick);
  }, [contextMenu, closeContextMenu]);

  const handleMenuAction = useCallback(
    (action: string) => {
      if (!contextMenu) return;

      const { tabKey } = contextMenu;

      setTabs((prev) => {
        const tabIndex = prev.findIndex((t) => t.key === tabKey);

        const currentTab = prev.find((t) => t.key === tabKey);

        const ensureActiveExists = (nextTabs: TabItem[]) => {
          if (nextTabs.some((t) => t.key === activeKey)) return;

          navigate(tabKey);
        };

        switch (action) {
          case "refresh":
            setRefreshingKey(tabKey);

            if (tabKey === activeKey) {
              setRefreshTrigger((p) => p + 1);

              refresh(tabKey).catch(() => {});

              setTimeout(() => navigate(tabKey, { replace: true }), 60);
            } else {
              refresh(tabKey).catch(() => {});
            }

            setTimeout(() => setRefreshingKey(null), 450);

            return prev;

          case "close":
            if (!currentTab?.closable || prev.length <= 1) return prev;

            drop(tabKey).catch(() => {});

            {
              const nextTabs = prev.filter((t) => t.key !== tabKey);

              if (tabKey === activeKey) {
                const nextIndex = Math.min(tabIndex, nextTabs.length - 1);

                navigate(nextTabs[nextIndex]?.key || HOME_PATH);
              }

              return nextTabs;
            }

          case "closeOthers": {
            const closed = prev.filter((t) => t.key !== tabKey && t.closable);

            closed.forEach((t) => drop(t.key).catch(() => {}));

            const nextTabs = prev.filter(
              (t) => t.key === tabKey || !t.closable
            );

            ensureActiveExists(nextTabs);

            return nextTabs;
          }

          case "closeLeft": {
            const closed = prev.filter((t, i) => i < tabIndex && t.closable);

            closed.forEach((t) => drop(t.key).catch(() => {}));

            const nextTabs = prev.filter(
              (t, i) => i >= tabIndex || !t.closable
            );

            ensureActiveExists(nextTabs);

            return nextTabs;
          }

          case "closeRight": {
            const closed = prev.filter((t, i) => i > tabIndex && t.closable);

            closed.forEach((t) => drop(t.key).catch(() => {}));

            const nextTabs = prev.filter(
              (t, i) => i <= tabIndex || !t.closable
            );

            ensureActiveExists(nextTabs);

            return nextTabs;
          }

          case "closeAll": {
            const closed = prev.filter((t) => t.closable);

            closed.forEach((t) => drop(t.key).catch(() => {}));

            const homeTab =
              prev.find((t) => !t.closable) ||
              ({
                key: HOME_PATH,

                path: HOME_PATH,

                title: "Dashboard",

                closable: false,
              } as TabItem);

            navigate(homeTab.key);

            return [homeTab];
          }

          default:
            return prev;
        }
      });

      closeContextMenu();
    },

    [contextMenu, activeKey, closeContextMenu, drop, refresh, navigate]
  );

  const scrollTabs = useCallback(
    (direction: "left" | "right") => {
      const container = tabsListRef.current;

      if (!container) return;

      container.scrollBy({
        left: direction === "left" ? -200 : 200,

        behavior: "smooth",
      });

      setTimeout(checkScrollable, 260);
    },

    [checkScrollable]
  );

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      const container = tabsListRef.current;

      if (!container) return;

      e.preventDefault();

      container.scrollLeft += e.deltaY;

      checkScrollable();
    },

    [checkScrollable]
  );

  const contextMenuItems = useMemo(() => {
    if (!contextMenu) return [];

    const tabIndex = tabs.findIndex((t) => t.key === contextMenu.tabKey);

    const hasLeft = tabs.slice(0, tabIndex).some((t) => t.closable);

    const hasRight = tabs.slice(tabIndex + 1).some((t) => t.closable);

    const hasOthers =
      tabs.filter((t) => t.key !== contextMenu.tabKey && t.closable).length > 0;

    const currentTab = tabs.find((t) => t.key === contextMenu.tabKey);

    return [
      { key: "refresh", icon: <ReloadOutlined />, label: "刷新" },

      { key: "divider1", divider: true },

      {
        key: "close",

        icon: <CloseOutlined />,

        label: "关闭",

        disabled: !currentTab?.closable || tabs.length <= 1,
      },

      {
        key: "closeOthers",

        icon: <CloseCircleOutlined />,

        label: "关闭其他",

        disabled: !hasOthers,
      },

      {
        key: "closeLeft",

        icon: <VerticalRightOutlined />,

        label: "关闭左侧",

        disabled: !hasLeft,
      },

      {
        key: "closeRight",

        icon: <VerticalLeftOutlined />,

        label: "关闭右侧",

        disabled: !hasRight,
      },

      { key: "divider2", divider: true },

      {
        key: "closeAll",

        icon: <MinusOutlined />,

        label: "关闭所有",

        danger: true,

        disabled: tabs.every((t) => !t.closable),
      },
    ];
  }, [contextMenu, tabs]);

  return (
    <div
      style={{
        display: "flex",

        flexDirection: "column",

        height: "100vh",

        overflow: "hidden",
      }}
    >
      <div className="tabs-view-container">
        <div
          className={`scroll-btn ${!canScrollLeft ? "disabled" : ""}`}
          onClick={() => scrollTabs("left")}
        >
          <LeftOutlined />
        </div>

        <div className="tabs-scroll-wrapper">
          <div
            ref={tabsListRef}
            className="tabs-list"
            onWheel={handleWheel}
            onScroll={checkScrollable}
          >
            {tabs.map((tab) => (
              <div
                key={tab.key}
                className={`tab-item ${tab.key === activeKey ? "active" : ""}`}
                onClick={() => handleTabClick(tab.key)}
                onContextMenu={(e) => handleContextMenu(e, tab.key)}
              >
                <span className="tab-title">{tab.title}</span>

                <div className="tab-actions">
                  {tab.key === activeKey && (
                    <span
                      className={`refresh-btn ${
                        refreshingKey === tab.key ? "spinning" : ""
                      }`}
                      onClick={(e) => handleRefresh(tab.key, e)}
                    >
                      <ReloadOutlined />
                    </span>
                  )}

                  {tab.closable && tabs.length > 1 && (
                    <span
                      className="close-btn"
                      onClick={(e) => handleClose(tab.key, e)}
                    >
                      <CloseOutlined />
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className={`scroll-btn ${!canScrollRight ? "disabled" : ""}`}
          onClick={() => scrollTabs("right")}
        >
          <RightOutlined />
        </div>
      </div>

      <div
        className="tabs-content-wrapper"
        style={{ flex: 1, overflow: "auto" }}
      >
        <div
          key={refreshTrigger}
          style={{
            transition: "opacity 0.2s ease-in-out, transform 0.2s ease-in-out",
            opacity: isTransitioning ? 0 : 1,
            transform: isTransitioning ? "translateX(-50px)" : "translateX(0)",
            position: "relative",
          }}
        >
          {children}
        </div>
      </div>

      {contextMenu && (
        <div
          className="context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          {contextMenuItems.map((item) =>
            item.divider ? (
              <div key={item.key} className="menu-divider" />
            ) : (
              <div
                key={item.key}
                className={`menu-item ${item.danger ? "danger" : ""} ${
                  item.disabled ? "disabled" : ""
                }`}
                onClick={() => !item.disabled && handleMenuAction(item.key)}
              >
                {item.icon}

                {item.label}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(TabsView);
